import useClipboard from "vue-clipboard3";
import { useUploadStore } from "@/store/uploadStore";
import { storeToRefs } from "pinia";
import { ref, computed } from "vue";
import { useQRCode } from "./useQRCode";
import { useLogin } from "../login/useLogin";
import { useMidStore } from "@/store/midStore";
import Api from "@/model/api";

// 弹窗场景
const DialogSceneEnum = {
  TEMP_FILE: Symbol("temp_file"),
  PERMANENT_FILE: Symbol("permanent_file"),
};

// 当前场景
const uploadDialogSceneType = ref(DialogSceneEnum.PERMANENT_FILE);

// 弹窗标题
const dialogTitle = computed(() =>
  uploadDialogSceneType.value === DialogSceneEnum.TEMP_FILE
    ? "临时云链已生成（7 日有效）"
    : "分享文件，就这么简单"
);

// 弹窗副标题
const dialogSubtitle = computed(() =>
  uploadDialogSceneType.value === DialogSceneEnum.TEMP_FILE
    ? "链接有效期：剩余 7 日"
    : "永久云链已生成"
);

// 是否显示操作按钮
const showActionButtons = computed(
  () => uploadDialogSceneType.value === DialogSceneEnum.PERMANENT_FILE
);

// 二维码
const { downloadQRCode, downloadAllQRCode } = useQRCode();

export const useUploadDialog = () => {
  // 上传状态
  const uploadStore = useUploadStore();
  const { isShowUploadDialog, uploadedFileList } = storeToRefs(uploadStore);

  // mid store
  const midStore = useMidStore();

  // 登录相关
  const {
    action: { openLoginDialog },
    state: { LoginSceneEnum, loginSceneType, isLogin },
  } = useLogin();

  // 监听登录弹窗状态变化
  const handleLoginDialogChange = async (newVal) => {
    if (
      !newVal && // 弹窗关闭
      loginSceneType.value === LoginSceneEnum.UPLOAD &&
      !isShowUploadDialog.value
    ) {
      // 登录成功
      if (isLogin.value) {
        await convertTempToPermanentFiles();
        openUploadDialog(DialogSceneEnum.PERMANENT_FILE);
      }
      // 取消登录
      else {
        openUploadDialog(DialogSceneEnum.TEMP_FILE);
      }
    }
  };

  // 打开弹窗
  const openUploadDialog = (scene = DialogSceneEnum.PERMANENT_FILE) => {
    uploadDialogSceneType.value = scene;
    isShowUploadDialog.value = true;
  };

  // 转换临时文件为永久文件
  const convertTempToPermanentFiles = async () => {
    try {
      const mid = await midStore.useMid();
      await Api.convertTempFiles({
        object_ids: uploadedFileList.value.map(
          (uploadedFile) => uploadedFile.object_id
        ),
        machine_id: mid,
      });
    } catch (error) {
      ElMessage.error("转换临时文件失败");
    }
  };

  // 点击登录按钮
  const handleLoginClick = () => {
    isShowUploadDialog.value = false;
    openLoginDialog(LoginSceneEnum.UPLOAD);
  };

  // 登录成功的回调处理
  const handleLoginSuccess = async () => {
    await convertTempToPermanentFiles();
  };

  // 完成上传后，复制 Url 按钮的文本
  const copyUrlBtnText = ref("复制");

  // 点击复制按钮
  const copyUrl = async (url) => {
    const { toClipboard } = useClipboard();
    try {
      await toClipboard(url);
      copyUrlBtnText.value = "已复制";
      ElMessage({
        message: "链接已复制",
        type: "success",
      });
    } catch (error) {
      ElMessage({
        message: "链接复制失败",
        type: "error",
      });
    }
  };

  // 复制所有文件链接
  const copyAllUrl = async () => {
    let copyText = "";
    uploadedFileList.value.forEach(
      (link) => (copyText += `文件名：${link.name}\n云链接：${link.url}\n\n`)
    );
    const { toClipboard } = useClipboard();
    try {
      await toClipboard(
        "简单云链 easylink.cc：轻松上传，简单分享\n\n" + copyText
      );
      ElMessage({
        message: "全部链接已复制",
        type: "success",
      });
    } catch (error) {
      ElMessage({
        message: "链接复制失败",
        type: "error",
      });
    }
  };

  // 关闭上传成功弹窗
  const closeUploadDialog = async () => {
    if (uploadDialogSceneType.value === DialogSceneEnum.PERMANENT_FILE) {
      isShowUploadDialog.value = false;
      return;
    }

    try {
      const action = await ElMessageBox.confirm(
        "该弹窗仅出现一次，请确保复制并保存好文件链接",
        "是否关闭弹窗？",
        {
          distinguishCancelAndClose: true,
          confirmButtonText: "确认",
          cancelButtonText: "取消",
          type: "warning",
          showClose: true,
        }
      );
      if (action === "confirm") {
        isShowUploadDialog.value = false;
      }
    } catch (error) {
      // 点击右上角关闭按钮时，不做任何操作
    }
  };

  return {
    // 状态
    state: {
      copyUrlBtnText,
      isShowUploadDialog,
      uploadedFileList,
      dialogTitle,
      dialogSubtitle,
      showActionButtons,
      DialogSceneEnum,
      uploadDialogSceneType,
    },

    // 方法
    action: {
      copyUrl,
      copyAllUrl,
      closeUploadDialog,
      downloadAllQRCode: () => downloadAllQRCode(uploadedFileList.value),
      downloadQRCode,
      openUploadDialog,
      handleLoginClick,
      handleLoginSuccess,
      convertTempToPermanentFiles,
      handleLoginDialogChange,
    },
  };
};
